import { FileText, Image as ImageIcon, Lock } from 'lucide-react';

import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { TicketAttachment, TicketMessage } from '@/types/support';

export interface TicketThreadProps {
  messages: TicketMessage[];
  /** Whose screen this is — their own messages sit on the right. */
  viewerSide: 'SCHOOL' | 'SUPPORT';
  onOpenAttachment: (attachment: TicketAttachment) => void;
}

function sizeOf(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * The conversation on a ticket, oldest first — read like a chat, because it is
 * one.
 *
 * Each message says who wrote it and which side they are on. An internal note
 * (only ever present on our own panel) is drawn in the warning tone with a
 * lock, so nobody mistakes it for something the school can see.
 */
export function TicketThread({ messages, viewerSide, onOpenAttachment }: TicketThreadProps) {
  return (
    <ol className="flex flex-col gap-6" aria-label="Conversation">
      {messages.map((message) => {
        const isOwnSide = message.author.side === viewerSide;
        const initials = getInitials(message.author.name);
        const isSupport = message.author.side === 'SUPPORT';

        return (
          <li
            key={message.uuid}
            className={cn('flex items-start gap-3', isOwnSide ? 'flex-row-reverse' : 'flex-row')}
          >
            {/* User Avatar */}
            <div
              className={cn(
                'grid h-8 w-8 shrink-0 place-items-center rounded-full text-xs font-bold shadow-2xs select-none',
                isOwnSide
                  ? 'bg-gradient-to-tr from-blue-600 to-indigo-600 text-white'
                  : isSupport
                    ? 'bg-gradient-to-tr from-teal-600 to-emerald-600 text-white'
                    : 'bg-slate-700 text-white',
              )}
              title={`${message.author.name} (${isSupport ? 'Support team' : 'School'})`}
            >
              {initials}
            </div>

            {/* Bubble Container */}
            <div className={cn('flex flex-col gap-1.5 max-w-[85%] sm:max-w-[78%] lg:max-w-[70%]', isOwnSide ? 'items-end' : 'items-start')}>
              {/* Author Meta */}
              <p className={cn('flex flex-wrap items-center gap-1.5 px-1 text-[11.5px] text-ink-3', isOwnSide ? 'justify-end' : 'justify-start')}>
                <span className="font-semibold text-ink-2">{message.author.name}</span>
                <span aria-hidden>·</span>
                <span className={cn(
                  'rounded-full px-1.5 py-0.2 text-[10.5px] font-medium',
                  isSupport
                    ? 'bg-teal-50 dark:bg-teal-950/60 text-teal-700 dark:text-teal-300'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 text-[#2d00fa] dark:text-slate-300'
                )}>
                  {isSupport ? 'Support team' : 'School'}
                </span>
                <span aria-hidden>·</span>
                <time dateTime={message.createdAt}>{formatDateTime(message.createdAt)}</time>
              </p>

              {/* Chat Message Bubble */}
              <div
                className={cn(
                  'w-fit max-w-full rounded-2xl px-4 py-3 text-sm leading-relaxed transition-shadow',
                  message.isInternal
                    ? 'border border-amber-300 dark:border-amber-700/80 bg-amber-50/90 dark:bg-amber-950/50 text-ink shadow-xs'
                    : isOwnSide
                      ? 'border border-blue-200/90 dark:border-blue-800/60 bg-blue-50/70 dark:bg-blue-950/40 text-slate-900 dark:text-slate-100 rounded-tr-xs shadow-xs'
                      : 'border border-line bg-surface text-ink rounded-tl-xs shadow-xs',
                )}
              >
                {message.isInternal && (
                  <div className="mb-2 flex items-center gap-1.5 rounded-lg bg-amber-100/70 dark:bg-amber-900/40 px-2.5 py-1 text-2xs font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">
                    <Lock className="h-3 w-3" aria-hidden />
                    Internal note — not visible to the school
                  </div>
                )}
                {/* Line breaks kept as typed; never rendered as HTML. */}
                <p className="whitespace-pre-wrap break-words">{message.body}</p>

                {message.attachments.length > 0 && (
                  <ul className="mt-3 flex flex-wrap gap-2 pt-2 border-t border-line/60">
                    {message.attachments.map((attachment) => (
                      <li key={attachment.uuid}>
                        <button
                          type="button"
                          onClick={() => onOpenAttachment(attachment)}
                          className="inline-flex max-w-[16rem] items-center gap-2 rounded-xl border border-line bg-surface/90 px-3 py-1.5 text-xs text-ink-2 transition-all hover:border-accent hover:text-accent hover:shadow-2xs focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-line cursor-pointer"
                        >
                          {attachment.mimeType.startsWith('image/') ? (
                            <ImageIcon className="h-3.5 w-3.5 shrink-0 text-blue-500" aria-hidden />
                          ) : (
                            <FileText className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
                          )}
                          <span className="truncate font-medium">{attachment.fileName}</span>
                          <span className="shrink-0 text-[10.5px] text-ink-3">({sizeOf(attachment.sizeBytes)})</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
